import {Component, OnInit} from "@angular/core";
import {NgbActiveModal, NgbModal, NgbModalRef} from "@ng-bootstrap/ng-bootstrap";
import {UserService} from "../../../services/user.service";
import {
    BehaviorSubject,
    debounceTime,
    distinctUntilChanged,
    filter,
    firstValueFrom,
    from,
    mergeMap,
    Observable,
    OperatorFunction
} from "rxjs";
import {HttpClient} from "@angular/common/http";
import {o} from "odata";
import {
    Employee,
    FileAttachment, HubUser,
    Task,
    TaskPriority,
    TaskStatus,
    TasksV1Service,
    TaskType
} from "../../../apis/backend";
import {DefaultPriorityColor, PriorityColorMap} from "../utils/priority";
import {EstimateTextareaHeight} from "../../../utils/text";

export const TaskStatusDisplayValues: Record<TaskStatus, string> = {
    New: 'New',
    InProgress: 'In Progress',
    Complete: 'Complete'
};

@Component({
    selector: 'task-dialog',
    templateUrl: './task-dialog.component.html',
})
export class TaskDialogComponent implements OnInit {

    public Task: Task = <Task>{};
    public CurrentUser: HubUser;

    public TaskTypeValues: TaskType[];
    public TaskTypesLoaded = new BehaviorSubject<boolean>(false);

    public TaskStatusValues: TaskStatus[];
    public TaskStatusDisplayValues = TaskStatusDisplayValues;
    
    public TaskPriorityValues: TaskPriority[];
    
    constructor(public ActiveModal: NgbActiveModal,
                public HttpService: HttpClient,
                private _modalService: NgbModal,
                public TaskService: TasksV1Service,
                public UserService: UserService) {
        UserService.CurrentUser.subscribe((user: HubUser) => {
            this.CurrentUser = user;
        });
        
        firstValueFrom(this.TaskService.getTaskTypes())
            .then(taskTypes => {
                this.TaskTypeValues = taskTypes;
                this.TaskTypesLoaded.next(true);
            });

        this.TaskStatusValues = <TaskStatus[]>Object.values(TaskStatus);

        firstValueFrom(this.TaskService.getTaskPriorities())
            .then(taskPriorities => this.TaskPriorityValues = taskPriorities
                .sort((a, b) => b.Priority - a.Priority));
    }

    ngOnInit(): void {
        this.Task.AssignedToEmployee = this.Task.AssignedToEmployee || this.CurrentUser.EmployeeInfo;
        
        this.TaskTypesLoaded.subscribe({
            next: (loaded) => {
                if (loaded) {
                    // TODO: Define the default more explicitly.
                    this.Task.TaskType = this.Task.TaskType || this.TaskTypeValues[0];
                }
            }
        });
        
        this.Task.TaskStatus = this.Task.TaskStatus || TaskStatus.New;
        this.Task.Notes = this.Task.Notes || '';
    }
    
    public ParseDate(dateStr: string) {
        return new Date(dateStr);
    }

    public EmployeeSearch: OperatorFunction<string, readonly Employee[]> = (searchTerm: Observable<string>) => searchTerm.pipe(
        debounceTime(200),
        distinctUntilChanged(),
        filter(term => term.length >= 2),
        mergeMap(term => {
            term = term.toLowerCase().replace("'", "''");
            return from(o('/', { credentials: 'include' })
                .get('odata/employees')
                .fetch({
                    $filter: `contains(tolower(FirstName), '${term}') or contains(tolower(LastName), '${term}')`,
                    $top: 10
                })
                .then(response => {
                    return (<any>response).json();
                })
                .then(odata => {
                    return odata.value;
                }));
        })
    );
    
    public FormatEmployee(employee: Employee) {
        return employee.FirstName + ' ' + employee.LastName;
    }
    
    public CompareTaskTypes(taskType1: TaskType, taskType2: TaskType) {
        return taskType1?.TaskTypeID == taskType2?.TaskTypeID;
    }
    
    public CompareTaskPriorities(taskPriority1: TaskPriority, taskPriority2: TaskPriority) {
        return taskPriority1?.TaskPriorityID == taskPriority2?.TaskPriorityID;
    }
    
    public GetCurrentPriorityColor() {
        return PriorityColorMap[this.Task?.TaskPriority?.Priority] || DefaultPriorityColor;
    }
    
    public GetNotesInputHeight() {
        return EstimateTextareaHeight(this.Task.Notes || '');
    }

    public FileUploader() {
        return (file: File) => {
            return firstValueFrom(this.TaskService.uploadFileAttachment(
                this.Task.TaskID,
                file));
        }
    }

    public FileDeleter() {
        return (fileAttachment: FileAttachment) => {
            return firstValueFrom(this.TaskService.deleteFileAttachment(
                this.Task.TaskID,
                fileAttachment.FileAttachmentID));
        }
    }
    
    public CreateTask() {
        firstValueFrom(this.TaskService.createTask(this.Task))
            .then(task => this.Task = task);
    }
    
    public Save() {
        this.ActiveModal.close(this.Task);
    }
    
    public Cancel() {
        this.ActiveModal.dismiss();
    }
}